import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { useClinicContext } from './useClinicContext';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  HospitalInitResponse,
  HospitalSearchResponse,
  HospitalDetailResponse,
  HospitalSearchFilters,
  HospitalFormData,
  HospitalMutationResponse,
  HospitalDeleteResponse,
} from '@/pages/main/hospital/types';

// ==================== API Service Functions ====================

/**
 * Get service type header value
 */
const getServiceTypeHeader = (): string => {
  return localStorage.getItem('service_type') || SERVICE_TYPE_R2;
};

/**
 * Get initialization data (prefectures)
 */
const getInit = async (siteType: string, clinicNumber: string): Promise<HospitalInitResponse> => {
  const response = await apiClient.get<HospitalInitResponse>(
    clinicApiPath(siteType, clinicNumber, '/hospitals/init'),
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Search hospitals with filters
 */
const search = async (
  siteType: string,
  clinicNumber: string,
  filters: HospitalSearchFilters
): Promise<HospitalSearchResponse> => {
  // Clean up empty values
  const params: Record<string, string | number | boolean> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value;
    }
  });

  const response = await apiClient.get<HospitalSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/hospitals/search'),
    {
      params,
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Get hospital detail by ID
 */
const getDetail = async (
  siteType: string,
  clinicNumber: string,
  id: number
): Promise<HospitalDetailResponse> => {
  const response = await apiClient.get<HospitalDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/hospitals/${id}`),
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Get all hospitals (paginated)
 */
const getAll = async (
  siteType: string,
  clinicNumber: string,
  perPage: number = 20,
  page: number = 1
): Promise<HospitalSearchResponse> => {
  const response = await apiClient.get<HospitalSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/hospitals'),
    {
      params: { per_page: perPage, page },
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Create a new hospital
 */
const create = async (
  siteType: string,
  clinicNumber: string,
  data: HospitalFormData
): Promise<HospitalMutationResponse> => {
  const response = await apiClient.post<HospitalMutationResponse>(
    clinicApiPath(siteType, clinicNumber, '/hospitals'),
    data,
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Update an existing hospital
 */
const update = async (
  siteType: string,
  clinicNumber: string,
  id: number,
  data: HospitalFormData
): Promise<HospitalMutationResponse> => {
  const response = await apiClient.put<HospitalMutationResponse>(
    clinicApiPath(siteType, clinicNumber, `/hospitals/${id}`),
    data,
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Delete a hospital
 */
const deleteHospital = async (
  siteType: string,
  clinicNumber: string,
  id: number
): Promise<HospitalDeleteResponse> => {
  const response = await apiClient.delete<HospitalDeleteResponse>(
    clinicApiPath(siteType, clinicNumber, `/hospitals/${id}`),
    {
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );
  return response.data;
};

/**
 * Export hospitals to CSV
 */
const exportCsv = async (
  siteType: string,
  clinicNumber: string,
  filters?: HospitalSearchFilters
): Promise<void> => {
  // Clean up empty values
  const params: Record<string, string | number | boolean> = {};
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value;
      }
    });
  }

  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, '/hospitals/export-csv'),
    {
      params,
      responseType: 'blob',
      headers: {
        'X-Service-Type': getServiceTypeHeader(),
      },
    }
  );

  // If server returned JSON (e.g. error payload), throw so UI can show message
  const contentType = response.headers['content-type'] || '';
  if (contentType.includes('application/json')) {
    const text = await (response.data as Blob).text();
    const json = JSON.parse(text);
    const message = json?.message || json?.error || 'CSV出力に失敗しました。';
    throw new Error(message);
  }

  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'hospitals.csv';
  if (contentDisposition) {
    const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/i);
    if (utf8FilenameMatch && utf8FilenameMatch[1]) {
      try {
        filename = decodeURIComponent(utf8FilenameMatch[1]);
      } catch (e) {
        filename = utf8FilenameMatch[1];
      }
    } else if (match && match[1]) {
      try {
        // Handle case where UTF-8 characters are parsed by the browser as ISO-8859-1 (Latin1 mojibake)
        filename = decodeURIComponent(escape(match[1]));
      } catch (e) {
        try {
          filename = decodeURIComponent(match[1]);
        } catch (e2) {
          filename = match[1];
        }
      }
    }
  }

  // Create blob and download
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ==================== Hooks ====================

/**
 * Hook to get hospital initialization data
 */
export const useMainHospitalInit = () => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ['hospitals', 'init', tenant, clinicNumber],
    queryFn: () => getInit(tenant, clinicNumber),
    enabled: !!tenant && !!clinicNumber,
  });
};

/**
 * Hook to search hospitals
 */
export const useMainHospitalSearch = (filters: HospitalSearchFilters) => {
  const { tenant, clinicNumber } = useClinicContext();

  const query = useQuery({
    queryKey: ['hospitals', 'search', tenant, clinicNumber, filters],
    queryFn: () => search(tenant, clinicNumber, filters),
    enabled: !!tenant && !!clinicNumber,
    placeholderData: keepPreviousData,
  });

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = query;

  return { data, isLoading, isFetching, isError, isPending, refetch, error };
};

/**
 * Hook to get hospital detail
 */
export const useMainHospitalDetail = (
  id: number,
  options?: { showNotFoundToast?: boolean },
) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ['hospitals', 'detail', tenant, clinicNumber, id],
    queryFn: () => getDetail(tenant, clinicNumber, id),
    enabled: !!tenant && !!clinicNumber && !!id,
    meta: { showNotFoundToast: options?.showNotFoundToast ?? true },
  });
};

/**
 * Hook to get all hospitals (paginated)
 */
export const useMainHospitals = (perPage: number = 20, page: number = 1) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ['hospitals', 'list', tenant, clinicNumber, perPage, page],
    queryFn: () => getAll(tenant, clinicNumber, perPage, page),
    enabled: !!tenant && !!clinicNumber,
  });
};

/**
 * Hook to create a hospital
 */
export const useMainHospitalCreate = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();

  return useMutation({
    mutationFn: (data: HospitalFormData) => create(tenant, clinicNumber, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospitals'] });
    },
  });
};

/**
 * Hook to update a hospital
 */
export const useMainHospitalUpdate = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: HospitalFormData }) =>
      update(tenant, clinicNumber, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hospitals'] });
      queryClient.invalidateQueries({ queryKey: ['hospitals', 'detail', variables.id] });
    },
  });
};

/**
 * Hook to delete a hospital
 */
export const useMainHospitalDelete = () => {
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();

  return useMutation({
    mutationFn: (id: number) => deleteHospital(tenant, clinicNumber, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospitals'] });
    },
  });
};

/**
 * Hook to export hospitals to CSV
 */
export const useMainHospitalExportCsv = () => {
  const { tenant, clinicNumber } = useClinicContext();

  return useMutation({
    mutationFn: (filters?: HospitalSearchFilters) => exportCsv(tenant, clinicNumber, filters),
    meta: {
      errorMessage: 'CSV出力に失敗しました。',
    },
  });
};
